/** The four demo skill tags for MCCQE Part I (matches `mockedTags.mcc`). */
export const mccDemoSkillTags = [
	"Biostatistics",
	"Medicine",
	"Medical Ethics",
	"Psychiatry",
] as const;

export const mccqePart1PracticeItems = [
	{
		id: 50101,
		type: "MCSS",
		content: {
			stem: 'In a well-designed randomized controlled trial of a drug to treat chronic hepatitis, the case fatality rate was lower for patients who were given the drug. This result was followed by the notation "(P = .05)."\n\nWhich one of the following statements best applies?',
			choices: [
				{
					id: "a1b2c3d4-1001-4a01-b101-aabbccdd0001",
					stem: "Treated patients were 20 times less likely to die than untreated patients.",
					text: "Treated patients were 20 times less likely to die than untreated patients.",
					isCorrect: false,
					numFirstSelections: 18,
				},
				{
					id: "a1b2c3d4-1001-4a01-b101-aabbccdd0002",
					stem: "The chance is 1 in 20 that a difference would be found again if the trial were repeated.",
					text: "The chance is 1 in 20 that a difference would be found again if the trial were repeated.",
					isCorrect: false,
					numFirstSelections: 24,
				},
				{
					id: "a1b2c3d4-1001-4a01-b101-aabbccdd0003",
					stem: "The probability is 1 in 20 that the observed differences could have happened by chance alone.",
					text: "The probability is 1 in 20 that the observed differences could have happened by chance alone.",
					isCorrect: true,
					explanation:
						'**The P value of .05 means there is a 5% probability (1 in 20 chance) that the observed differences in case fatality rates between the treated and untreated groups could have occurred due to chance alone.** This is the correct interpretation of statistical significance at the P = .05 threshold.\n\nThis statistical significance suggests the results are unlikely to be due to random variation alone, supporting the efficacy of the drug in reducing mortality. Critically, the P value does **not** provide information about the magnitude of the effect.\n\n**Why the other options are incorrect:**\n\n- **"Treated patients were 20 times less likely to die than untreated patients"** — The P value says nothing about relative risk or magnitude of benefit. A P value of .05 does not imply a 20-fold reduction in risk; it only reflects the probability that chance explains the observed difference.\n\n- **"The chance is 1 in 20 that a difference would be found again if the trial were repeated"** — This is a common misinterpretation. The P value does not predict reproducibility of results in future trials. It reflects the probability of observing data at least as extreme as those observed, assuming the null hypothesis (no difference) is true.\n\n- **"An improved chance of survival was noted for 19 of 20 patients treated"** — This confuses the P value with individual patient outcomes. P = .05 is a population-level statistical statement, not a statement about individual patient responses.\n\n- **"The mortality rate for patients was less than 1 in 20"** — The P value does not measure or describe the absolute mortality rate.\n\n**Key Takeaways:**\n- **P = .05 means a 5% probability** that the observed result (or a more extreme one) occurred by chance if there were truly no treatment effect.\n- The P value tells you about the **strength of evidence against the null hypothesis**, not about effect size, clinical significance, or the probability of replication.\n- A statistically significant result (P ≤ .05) does not guarantee clinical significance — always consider effect size and confidence intervals.\n\n**Reference:**\nTenny S, Abdelgawad I. Statistical significance. In: *StatPearls*. StatPearls Publishing; Updated November 23, 2023.',
					numFirstSelections: 140,
				},
				{
					id: "a1b2c3d4-1001-4a01-b101-aabbccdd0004",
					stem: "An improved chance of survival was noted for 19 of 20 patients treated.",
					text: "An improved chance of survival was noted for 19 of 20 patients treated.",
					isCorrect: false,
					numFirstSelections: 22,
				},
				{
					id: "a1b2c3d4-1001-4a01-b101-aabbccdd0005",
					stem: "The mortality rate for patients was less than 1 in 20.",
					text: "The mortality rate for patients was less than 1 in 20.",
					isCorrect: false,
					numFirstSelections: 14,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 7001,
				type: "Skill",
				label: "Biostatistics",
				breadcrumb: "Biostatistics",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"P Value Interpretation",
				"Statistical Significance",
				"Research Methodology",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "a1b2c3d4-1001-4a01-b101-aabbccdd0003",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 50102,
		type: "MCSS",
		content: {
			stem: "A 25-year-old man presents to your clinic with a 2-month history of swelling, pain, and inflammation in his knee. He has been experiencing low back pain and morning stiffness for 2 years.\n\nWhich one of the following is most likely to confirm the diagnosis?",
			choices: [
				{
					id: "b2c3d4e5-2002-4b02-c202-bbccddee0001",
					stem: "Uric acid level",
					text: "Uric acid level",
					isCorrect: false,
					numFirstSelections: 20,
				},
				{
					id: "b2c3d4e5-2002-4b02-c202-bbccddee0002",
					stem: "Radiography of the sacroiliac joints",
					text: "Radiography of the sacroiliac joints",
					isCorrect: true,
					explanation:
						"**Radiography of the sacroiliac joints is the most likely investigation to confirm the diagnosis of ankylosing spondylitis**, which is the leading diagnosis in this clinical vignette. The combination of chronic low back pain with morning stiffness, peripheral joint involvement (knee), and age of onset in a young man is highly characteristic of ankylosing spondylitis — the prototypical spondyloarthropathy.\n\nSacroiliac joint radiographs can reveal **sacroiliitis**, the hallmark of ankylosing spondylitis, showing changes such as joint space narrowing, subchondral sclerosis, or erosions. These imaging findings confirm the diagnosis when the clinical picture is suggestive.\n\n**Why the other options are incorrect:**\n\n- **Uric acid level** — Elevated uric acid and its measurement are used to diagnose or support a diagnosis of **gout**, which typically presents with acute monoarthritis (commonly the first metatarsophalangeal joint), not with chronic low back pain and morning stiffness. Gout does not fit this clinical picture.\n\n- **Antinuclear antibodies (ANA) test** — ANA testing is most useful for diagnosing autoimmune conditions such as **systemic lupus erythematosus (SLE)** or mixed connective tissue disease. These conditions do not typically present with the spondyloarthropathy pattern seen here.\n\n- **Rheumatoid factor (RF) level** — Elevated RF is associated with **rheumatoid arthritis**, which classically affects small joints of the hands and feet symmetrically and does not typically cause inflammatory low back pain or sacroiliitis.\n\n- **Synovial fluid analysis** — While useful for diagnosing septic arthritis, crystal arthropathies (gout, pseudogout), or inflammatory arthritis, synovial fluid analysis does not provide information about the sacroiliac joints and would not confirm the spondyloarthropathy.\n\n**Key Takeaways:**\n- Young men with chronic inflammatory back pain, morning stiffness, and peripheral arthritis should be evaluated for **ankylosing spondylitis**.\n- **Sacroiliac joint radiography** is the cornerstone of diagnosis — sacroiliitis is the defining radiologic feature.\n- If radiographs are negative but clinical suspicion remains high, **MRI of the sacroiliac joints** is the preferred next step as it detects early inflammatory changes before structural damage is visible.\n\n**Reference:**\nOstergaard M, Lambert RG. Imaging in ankylosing spondylitis. *Therapeutic Advances in Musculoskeletal Disease*. 2012;4(4):301–311.",
					numFirstSelections: 145,
				},
				{
					id: "b2c3d4e5-2002-4b02-c202-bbccddee0003",
					stem: "Antinuclear antibodies test",
					text: "Antinuclear antibodies test",
					isCorrect: false,
					numFirstSelections: 18,
				},
				{
					id: "b2c3d4e5-2002-4b02-c202-bbccddee0004",
					stem: "Rheumatoid factor level",
					text: "Rheumatoid factor level",
					isCorrect: false,
					numFirstSelections: 26,
				},
				{
					id: "b2c3d4e5-2002-4b02-c202-bbccddee0005",
					stem: "Synovial fluid analysis",
					text: "Synovial fluid analysis",
					isCorrect: false,
					numFirstSelections: 16,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 7002,
				type: "Skill",
				label: "Medicine",
				breadcrumb: "Medicine",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Ankylosing Spondylitis",
				"Spondyloarthropathy",
				"Musculoskeletal Imaging",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "b2c3d4e5-2002-4b02-c202-bbccddee0002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 50103,
		type: "MCSS",
		content: {
			stem: "A 77-year-old man presents to your clinic because of urinary incontinence for the past 2 months. He says that his bladder always feels full, and he frequently loses bladder control during the day and night. For the past year, he has had to urinate every 2 to 3 hours and has had nocturia.\n\nWhich one of the following is the most likely diagnosis?",
			choices: [
				{
					id: "c3d4e5f6-3003-4c03-d303-ccddeeff0001",
					stem: "Neurogenic bladder",
					text: "Neurogenic bladder",
					isCorrect: false,
					numFirstSelections: 22,
				},
				{
					id: "c3d4e5f6-3003-4c03-d303-ccddeeff0002",
					stem: "Overflow incontinence",
					text: "Overflow incontinence",
					isCorrect: true,
					explanation:
						"**Overflow incontinence is the most likely diagnosis in this elderly man.** The classic presentation — a constant feeling of bladder fullness, frequent loss of bladder control day and night, increased urinary frequency, and nocturia — is characteristic of overflow incontinence, which occurs when the bladder is unable to empty properly and overfills, eventually leaking past the obstruction.\n\nIn an older man, the most common underlying cause is **benign prostatic hyperplasia (BPH)**, which creates bladder outlet obstruction. The progressive nature of urinary symptoms over a year before the incontinence developed is consistent with gradually worsening BPH.\n\n**Why the other options are incorrect:**\n\n- **Interstitial cystitis** — This chronic condition causes bladder pressure, pain, and urgency, but does **not** typically present with a constant feeling of bladder fullness or overflow-type incontinence. It is also far more common in women than men.\n\n- **Prostate cancer** — While prostate cancer can cause urinary symptoms similar to BPH, it is less likely to be the cause of **overflow incontinence** specifically without other systemic signs. The presentation is more consistent with obstructive BPH.\n\n- **Neurogenic bladder** — This results from nerve damage (e.g., spinal cord injury, multiple sclerosis, diabetic neuropathy) and could produce overflow incontinence. However, the vignette does not mention any neurologic history or findings, making this a less likely primary diagnosis than BPH-related outflow obstruction.\n\n- **Chronic cystitis** — A persistent bladder infection would present more prominently with dysuria (painful urination), urgency, and pyuria rather than the overflow pattern described here.\n\n**Key Takeaways:**\n- **Overflow incontinence in an older man = think BPH first.** Bladder outlet obstruction leads to incomplete emptying, progressive distension, and overflow leakage.\n- The hallmarks are: constant sensation of bladder fullness, frequent small-volume voids, and involuntary leakage.\n- Initial workup includes digital rectal exam, urinalysis, PSA, and post-void residual volume measurement.\n\n**Reference:**\nLeslie SW, Tran LN, Puckett Y. Urinary incontinence. In: *StatPearls*. StatPearls Publishing; Updated August 11, 2024.",
					numFirstSelections: 148,
				},
				{
					id: "c3d4e5f6-3003-4c03-d303-ccddeeff0003",
					stem: "Interstitial cystitis",
					text: "Interstitial cystitis",
					isCorrect: false,
					numFirstSelections: 16,
				},
				{
					id: "c3d4e5f6-3003-4c03-d303-ccddeeff0004",
					stem: "Prostate cancer",
					text: "Prostate cancer",
					isCorrect: false,
					numFirstSelections: 20,
				},
				{
					id: "c3d4e5f6-3003-4c03-d303-ccddeeff0005",
					stem: "Chronic cystitis",
					text: "Chronic cystitis",
					isCorrect: false,
					numFirstSelections: 12,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 7002,
				type: "Skill",
				label: "Medicine",
				breadcrumb: "Medicine",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Urinary Incontinence",
				"Overflow Incontinence",
				"Benign Prostatic Hyperplasia",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "c3d4e5f6-3003-4c03-d303-ccddeeff0002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 50104,
		type: "MCSS",
		content: {
			stem: "A 42-year-old woman presents to your clinic and shares that her husband is physically abusing her. A few days later, you receive a call from a police officer who says she is investigating a complaint made by your patient against her husband. The police officer explains that she needs medical information from you for the investigation.\n\nWhich one of the following is the best next step?",
			choices: [
				{
					id: "d4e5f6a7-4004-4d04-e404-ddeeffaa0001",
					stem: "Send the police officer a medicolegal report.",
					text: "Send the police officer a medicolegal report.",
					isCorrect: false,
					numFirstSelections: 24,
				},
				{
					id: "d4e5f6a7-4004-4d04-e404-ddeeffaa0002",
					stem: "Contact the patient to discuss the request.",
					text: "Contact the patient to discuss the request.",
					isCorrect: true,
					explanation:
						"**Contacting the patient to discuss the police officer's request is the correct next step.** This approach respects patient autonomy and confidentiality, two foundational principles of medical ethics in Canada. Any disclosure of medical information to law enforcement must be preceded by the patient's informed consent — unless a specific legal obligation to disclose (e.g., mandatory reporting legislation) applies.\n\nIn this scenario, there is no mandatory reporting requirement triggered. The patient has already filed a complaint with police herself, which indicates she may be willing to have her medical information shared — but her explicit consent must be obtained first. Contacting the patient allows her to:\n1. Be informed that the police are requesting her records\n2. Decide whether she consents to disclosure\n3. Maintain control over her personal health information during a vulnerable time\n\n**Why the other options are incorrect:**\n\n- **\"Call the police officer after you review the patient's file\"** — Reviewing the file and calling back does not address the fundamental issue of patient consent. Disclosing any information without consent violates confidentiality, regardless of whether you review the chart first.\n\n- **\"Send the police officer a medicolegal report\"** — Sending a report without the patient's consent is a direct breach of physician-patient confidentiality. Even in domestic violence cases, physician disclosure to police without consent is not legally mandated in most Canadian provinces unless a mandatory reporting obligation applies (e.g., child protection, gunshot wounds).\n\n- **\"Refuse to speak with the police officer\"** — While refusing protects confidentiality in the short term, it does not address whether the patient wishes to cooperate with the investigation. This response fails to involve the patient and is not the most helpful course of action.\n\n**Key Takeaways:**\n- **Patient consent is required before disclosing medical information to law enforcement** in the absence of mandatory reporting obligations.\n- Always **involve the patient** in decisions about disclosure of their medical records, especially in sensitive situations involving domestic violence.\n- The physician's role is to support the patient's autonomy and safety — contacting her allows her to make an informed decision about participating in the investigation.\n\n**Reference:**\nCanadian Medical Protective Association. *Physician interactions with police*. Published March 2011. Revised June 2023.",
					numFirstSelections: 152,
				},
				{
					id: "d4e5f6a7-4004-4d04-e404-ddeeffaa0003",
					stem: "Call the police officer after you review the patient's file.",
					text: "Call the police officer after you review the patient's file.",
					isCorrect: false,
					numFirstSelections: 28,
				},
				{
					id: "d4e5f6a7-4004-4d04-e404-ddeeffaa0004",
					stem: "Refuse to speak with the police officer.",
					text: "Refuse to speak with the police officer.",
					isCorrect: false,
					numFirstSelections: 14,
				},
				{
					id: "d4e5f6a7-4004-4d04-e404-ddeeffaa0005",
					stem: "Provide a verbal summary of the patient's injuries to the police officer over the phone.",
					text: "Provide a verbal summary of the patient's injuries to the police officer over the phone.",
					isCorrect: false,
					numFirstSelections: 10,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 7003,
				type: "Skill",
				label: "Medical Ethics",
				breadcrumb: "Medical Ethics",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Patient Confidentiality",
				"Physician-Patient Relationship",
				"Medical Ethics and Law",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "d4e5f6a7-4004-4d04-e404-ddeeffaa0002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 50105,
		type: "MCSS",
		content: {
			stem: "A 26-year-old man presents to your clinic with anejaculation, which he has had since he started taking paroxetine. He has major depressive disorder. He would like to switch to a different antidepressant.\n\nWhich one of the following medications is the most indicated?",
			choices: [
				{
					id: "e5f6a7b8-5005-4e05-f505-eeffaabb0001",
					stem: "Fluoxetine",
					text: "Fluoxetine",
					isCorrect: false,
					numFirstSelections: 20,
				},
				{
					id: "e5f6a7b8-5005-4e05-f505-eeffaabb0002",
					stem: "Clomipramine",
					text: "Clomipramine",
					isCorrect: false,
					numFirstSelections: 14,
				},
				{
					id: "e5f6a7b8-5005-4e05-f505-eeffaabb0003",
					stem: "Phenelzine",
					text: "Phenelzine",
					isCorrect: false,
					numFirstSelections: 12,
				},
				{
					id: "e5f6a7b8-5005-4e05-f505-eeffaabb0004",
					stem: "Bupropion",
					text: "Bupropion",
					isCorrect: true,
					explanation:
						"**Bupropion is the most indicated antidepressant for a patient experiencing SSRI-induced anejaculation.** Paroxetine is a selective serotonin reuptake inhibitor (SSRI), and SSRI-induced sexual dysfunction — including anejaculation, delayed ejaculation, decreased libido, and anorgasmia — is one of the most common and bothersome adverse effects of this drug class.\n\nBupropion is an **atypical antidepressant** that works by inhibiting the reuptake of **norepinephrine and dopamine**, with minimal effect on serotonin. Because sexual dysfunction is predominantly a serotonin-mediated adverse effect, bupropion carries a significantly **lower risk of sexual dysfunction** compared with SSRIs and SNRIs. It is effective for major depressive disorder and represents the most appropriate switch in this clinical context.\n\n**Why the other options are incorrect:**\n\n- **Fluoxetine** — Another SSRI with a long half-life. While it is sometimes used to taper patients off shorter-acting SSRIs, it carries the **same risk of sexual dysfunction** as paroxetine. Switching to fluoxetine would not resolve the anejaculation.\n\n- **Venlafaxine** — A serotonin-norepinephrine reuptake inhibitor (SNRI). Due to its significant serotonin reuptake inhibition, venlafaxine also carries a **substantial risk of sexual dysfunction** and would likely perpetuate the patient's anejaculation.\n\n- **Clomipramine** — A tricyclic antidepressant with potent serotonin reuptake inhibition. It is actually associated with one of the **highest rates of anorgasmia and ejaculatory dysfunction** among antidepressants. This would worsen the patient's complaint.\n\n- **Phenelzine** — A monoamine oxidase inhibitor (MAOI) with a complex adverse effect profile, significant dietary restrictions (tyramine-free diet required), and many drug-drug interactions. It is not a reasonable first-line switch given the patient's specific complaint.\n\n**Key Takeaways:**\n- **SSRI-induced sexual dysfunction** is common and often leads patients to discontinue medication — addressing it is clinically important.\n- **Bupropion** is the preferred switch for patients with SSRI-induced sexual dysfunction, due to its dopamine/norepinephrine mechanism and low serotonergic activity.\n- Mirtazapine is another option with lower rates of sexual dysfunction, though it causes significant sedation and weight gain.\n\n**Reference:**\nLipman K, Betterly H, Botros M. Improvement in selective serotonin reuptake inhibitor-associated sexual dysfunction with buspirone: Examining the evidence. *Cureus*. 2024;16(4):e57981.",
					numFirstSelections: 148,
				},
				{
					id: "e5f6a7b8-5005-4e05-f505-eeffaabb0005",
					stem: "Venlafaxine",
					text: "Venlafaxine",
					isCorrect: false,
					numFirstSelections: 24,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 7004,
				type: "Skill",
				label: "Psychiatry",
				breadcrumb: "Psychiatry",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: ["Antidepressants", "SSRI Adverse Effects", "Sexual Dysfunction"],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "e5f6a7b8-5005-4e05-f505-eeffaabb0004",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 50106,
		type: "MCSS",
		content: {
			stem: "A 24-year-old man presents to your clinic for the first time and reports having had suicidal thoughts for most of his life. He says that he and his girlfriend recently broke up, and he is feeling alone and abandoned. He also notes that he often feels empty and his mood is very unstable. He says \"People think I'm a little bit crazy. They never know what I'm going to do.\"\n\nThese symptoms are indicative of which one of the following personality disorders?",
			choices: [
				{
					id: "f6a7b8c9-6006-4f06-a606-ffaabbcc0001",
					stem: "Antisocial",
					text: "Antisocial",
					isCorrect: false,
					numFirstSelections: 18,
				},
				{
					id: "f6a7b8c9-6006-4f06-a606-ffaabbcc0002",
					stem: "Histrionic",
					text: "Histrionic",
					isCorrect: false,
					numFirstSelections: 14,
				},
				{
					id: "f6a7b8c9-6006-4f06-a606-ffaabbcc0003",
					stem: "Borderline",
					text: "Borderline",
					isCorrect: true,
					explanation:
						"**Borderline personality disorder (BPD) is the most consistent diagnosis with this clinical presentation.** The key features in this vignette that point to BPD include:\n\n1. **Chronic suicidal ideation** — Recurrent suicidal thoughts or self-harming behaviours are a core diagnostic criterion of BPD (DSM-5 Criterion 5)\n2. **Fear of abandonment** — Intense fear of real or imagined abandonment following the relationship breakup (Criterion 1)\n3. **Chronic feelings of emptiness** — A persistent, subjective sense of inner void (Criterion 7)\n4. **Markedly unstable mood** — Affective instability due to a marked reactivity of mood (Criterion 6)\n5. **Impulsive and unpredictable behaviour** — Suggested by his comment that others \"never know what I'm going to do\" (Criterion 4)\n\n**Why the other options are incorrect:**\n\n- **Antisocial personality disorder** — Characterized by a pervasive pattern of violation of others' rights, deceitfulness, lack of remorse, and callous disregard for others. While impulsivity is shared with BPD, the core features of antisocial PD (exploitation, criminality, aggression) are not present here.\n\n- **Narcissistic personality disorder** — Marked by grandiosity, a need for admiration, and lack of empathy. The patient shows vulnerability and fear of abandonment — the opposite of narcissistic grandiosity.\n\n- **Schizotypal personality disorder** — Characterized by eccentric behaviour, magical thinking, and perceptual distortions (e.g., ideas of reference). These features are not described in this vignette.\n\n- **Histrionic personality disorder** — Involves excessive emotionality and attention-seeking behaviour. While some overlap exists, the hallmark features of BPD (chronic emptiness, suicidality, abandonment fear, identity disturbance) are much more prominent here.\n\n**Key Takeaways:**\n- **BPD hallmarks**: chronic suicidality, intense abandonment fear, identity disturbance, chronic emptiness, and marked mood instability.\n- BPD is **commonly misidentified** as depression, bipolar disorder, or other personality disorders — accurate diagnosis requires a comprehensive longitudinal assessment.\n- **Dialectical Behaviour Therapy (DBT)** is the evidence-based first-line treatment for BPD.\n\n**Reference:**\nAmerican Psychiatric Association. *Diagnostic and Statistical Manual of Mental Disorders, Fifth Edition, Text Revision (DSM-5-TR)*. American Psychiatric Association Publishing; 2022:862.",
					numFirstSelections: 146,
				},
				{
					id: "f6a7b8c9-6006-4f06-a606-ffaabbcc0004",
					stem: "Narcissistic",
					text: "Narcissistic",
					isCorrect: false,
					numFirstSelections: 22,
				},
				{
					id: "f6a7b8c9-6006-4f06-a606-ffaabbcc0005",
					stem: "Schizotypal",
					text: "Schizotypal",
					isCorrect: false,
					numFirstSelections: 16,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 7004,
				type: "Skill",
				label: "Psychiatry",
				breadcrumb: "Psychiatry",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Personality Disorders",
				"Borderline Personality Disorder",
				"Psychiatry Diagnosis",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "f6a7b8c9-6006-4f06-a606-ffaabbcc0003",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 50107,
		type: "MCSS",
		content: {
			stem: "A 42-year-old man presents to your clinic for follow-up. He has been experiencing paranoid ideation that began 8 months ago. After a few weeks of treatment, he was able to return to work as a teacher. His delusions were limited to the belief that coworkers were sending messages about him to each other through various hand signals. He has had no auditory hallucinations and has retained a good range of affect.\n\nWhich one of the following is the most likely diagnosis?",
			choices: [
				{
					id: "a7b8c9d0-7007-4a07-b707-aabbccdd0001",
					stem: "Schizophrenia",
					text: "Schizophrenia",
					isCorrect: false,
					numFirstSelections: 26,
				},
				{
					id: "a7b8c9d0-7007-4a07-b707-aabbccdd0002",
					stem: "Delusional disorder",
					text: "Delusional disorder",
					isCorrect: true,
					explanation:
						"**Delusional disorder is the most likely diagnosis in this case.** The clinical features are highly specific: a single, non-bizarre delusion (the belief that coworkers communicate about him through hand signals) persisting for over 8 months, without hallucinations, without significant blunting of affect, and with preserved overall functioning (he returned to work).\n\nThe DSM-5 criteria for delusional disorder include:\n1. One or more **delusions** for ≥1 month\n2. Criterion A for schizophrenia (including hallucinations) has **never been met**\n3. **Functioning is not markedly impaired** outside of the delusion\n4. No manic/depressive episodes concurrent with delusions\n5. Not due to a substance or medical condition\n\nAll of these are satisfied in this vignette.\n\n**Why the other options are incorrect:**\n\n- **Schizophrenia** — Requires ≥2 positive symptoms (delusions, hallucinations, disorganized speech, grossly disorganized behaviour, or negative symptoms) for ≥6 months, including at least one month of active-phase symptoms. This patient has **only one positive symptom** (delusions), **no hallucinations**, and **preserved affect**, ruling out schizophrenia.\n\n- **Schizoaffective disorder** — Requires a major mood episode (depressive or manic) concurrent with symptoms meeting Criterion A for schizophrenia, plus psychotic symptoms in the absence of mood episodes. Neither mood episodes nor multiple psychotic symptoms are present here.\n\n- **Body dysmorphic disorder** — A preoccupation with perceived defects in physical appearance. The delusions here are **not appearance-related**.\n\n- **Major depressive disorder with psychotic features** — Would require a major depressive episode as the primary diagnosis, with psychotic features emerging during the mood episode. No mood disorder is evident in this vignette.\n\n**Key Takeaways:**\n- **Delusional disorder = isolated, persistent delusion without other psychotic symptoms or significant functional impairment.**\n- The **preservation of affect and functioning** is a key distinguishing feature from schizophrenia.\n- Delusional disorder may respond to antipsychotics, but prognosis is variable.\n\n**Reference:**\nJoseph SM, Siddiqui W. Delusional disorder. In: *StatPearls*. StatPearls Publishing; March 27, 2023.",
					numFirstSelections: 144,
				},
				{
					id: "a7b8c9d0-7007-4a07-b707-aabbccdd0003",
					stem: "Schizoaffective disorder",
					text: "Schizoaffective disorder",
					isCorrect: false,
					numFirstSelections: 20,
				},
				{
					id: "a7b8c9d0-7007-4a07-b707-aabbccdd0004",
					stem: "Body dysmorphic disorder",
					text: "Body dysmorphic disorder",
					isCorrect: false,
					numFirstSelections: 12,
				},
				{
					id: "a7b8c9d0-7007-4a07-b707-aabbccdd0005",
					stem: "Major depressive disorder with psychotic features",
					text: "Major depressive disorder with psychotic features",
					isCorrect: false,
					numFirstSelections: 16,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 7004,
				type: "Skill",
				label: "Psychiatry",
				breadcrumb: "Psychiatry",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Delusional Disorder",
				"Psychotic Disorders",
				"Differential Diagnosis in Psychiatry",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "a7b8c9d0-7007-4a07-b707-aabbccdd0002",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 50108,
		type: "MCSS",
		content: {
			stem: "An 18-year-old woman is in hospital and has delivered her first child weighing 2500 g following a short second-stage delivery. The patient has a history of heavy menstrual bleeding. Two hours after delivery, she has profuse vaginal bleeding and hypotension. Her fundus is firm and 6 cm below the umbilicus.\n\nWhich one of the following is the most likely cause of the increased bleeding in this patient?",
			choices: [
				{
					id: "b8c9d0e1-8008-4b08-c808-bbccddee0001",
					stem: "Uterine atony",
					text: "Uterine atony",
					isCorrect: false,
					numFirstSelections: 30,
				},
				{
					id: "b8c9d0e1-8008-4b08-c808-bbccddee0002",
					stem: "Uterine rupture",
					text: "Uterine rupture",
					isCorrect: false,
					numFirstSelections: 16,
				},
				{
					id: "b8c9d0e1-8008-4b08-c808-bbccddee0003",
					stem: "Coagulopathy",
					text: "Coagulopathy",
					isCorrect: true,
					explanation:
						"**Coagulopathy is the most likely cause of the postpartum hemorrhage in this patient.** The critical clue is the history of **heavy menstrual bleeding** throughout her life, which suggests a **pre-existing bleeding disorder** (e.g., von Willebrand disease, platelet dysfunction, or other coagulopathy). In the context of the physiologic stress of childbirth, an underlying coagulopathy can be unmasked or exacerbated, leading to profuse hemorrhage.\n\nFurther supporting coagulopathy as the diagnosis is the **firm uterine fundus** — this directly rules out uterine atony, which is the most common cause of postpartum hemorrhage but would present with a **soft, boggy uterus**.\n\n**Why the other options are incorrect:**\n\n- **Uterine atony** — The **most common cause** of postpartum hemorrhage overall. However, uterine atony causes a soft, poorly contracted uterus. This patient's fundus is described as **firm**, definitively excluding atony as the etiology.\n\n- **Cervical laceration** — Lacerations can cause significant postpartum bleeding, but they are more commonly associated with **instrumental deliveries** (forceps, vacuum) or precipitous deliveries resulting in significant perineal or cervical trauma. The vignette does not describe these risk factors, and the history of pre-existing heavy menstrual bleeding makes coagulopathy a stronger explanation.\n\n- **Uterine rupture** — Would be expected to present with **severe abdominal pain, fetal distress, and signs of shock** often preceding the hemorrhage. A firm fundus also argues against rupture, which would typically result in an abnormal uterine contour or absence of a palpable fundus.\n\n- **Endometritis** — An infection of the uterine lining that typically develops **days after delivery**, not within 2 hours. It would not cause acute profuse hemorrhage immediately postpartum.\n\n**Key Takeaways:**\n- **A firm fundus in the setting of postpartum hemorrhage narrows the differential** away from atony toward laceration, retained products, or coagulopathy.\n- A **history of heavy menstrual bleeding** is a red flag for underlying coagulopathy (most commonly von Willebrand disease in young women).\n- Postpartum hemorrhage workup: assess uterine tone (atony?), inspect the birth canal (lacerations?), check placental delivery (retained products?), and assess coagulation (coagulopathy?).\n\n**References:**\nWormer KC, Jamil RT, Bryant SB. Postpartum hemorrhage. In: *StatPearls*. StatPearls Publishing; Updated July 19, 2024.",
					numFirstSelections: 142,
				},
				{
					id: "b8c9d0e1-8008-4b08-c808-bbccddee0004",
					stem: "Cervical laceration",
					text: "Cervical laceration",
					isCorrect: false,
					numFirstSelections: 22,
				},
				{
					id: "b8c9d0e1-8008-4b08-c808-bbccddee0005",
					stem: "Endometritis",
					text: "Endometritis",
					isCorrect: false,
					numFirstSelections: 8,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 7002,
				type: "Skill",
				label: "Medicine",
				breadcrumb: "Medicine",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Postpartum Hemorrhage",
				"Coagulopathy",
				"Obstetric Complications",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "b8c9d0e1-8008-4b08-c808-bbccddee0003",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 50109,
		type: "MCSS",
		content: {
			stem: "A 5-year-old boy is brought by his mother to your clinic because for the last few months he has been passing stools in his clothes several times a week. He says that he is unaware of it happening. The mother says that the stools he passes are not particularly hard. There are no other symptoms. Abdominal examination reveals a firm nontender mass in the left lower quadrant.\n\nWhich one of the following is the most appropriate recommendation?",
			choices: [
				{
					id: "c9d0e1f2-9009-4c09-d909-ccddee000001",
					stem: "Surgical intervention will most likely be needed.",
					text: "Surgical intervention will most likely be needed.",
					isCorrect: false,
					numFirstSelections: 14,
				},
				{
					id: "c9d0e1f2-9009-4c09-d909-ccddee000002",
					stem: "Magnetic resonance imaging of the spine should be performed.",
					text: "Magnetic resonance imaging of the spine should be performed.",
					isCorrect: false,
					numFirstSelections: 18,
				},
				{
					id: "c9d0e1f2-9009-4c09-d909-ccddee000003",
					stem: "Referral to a psychiatrist is necessary.",
					text: "Referral to a psychiatrist is necessary.",
					isCorrect: false,
					numFirstSelections: 10,
				},
				{
					id: "c9d0e1f2-9009-4c09-d909-ccddee000004",
					stem: "The condition requires treatment with laxatives.",
					text: "The condition requires treatment with laxatives.",
					isCorrect: true,
					explanation:
						"**Treatment with laxatives is the most appropriate recommendation** for this child. The clinical picture describes **functional fecal impaction with overflow soiling (encopresis)**, which is one of the most common causes of fecal incontinence in children.\n\nThe key findings supporting this diagnosis are:\n- **Involuntary, unaware soiling** in a 5-year-old (beyond the age of expected continence)\n- Stools are **not particularly hard** — this is the hallmark of overflow soiling, where liquid stool leaks around a large impacted fecal mass\n- A **firm, nontender left lower quadrant mass** — consistent with a retained fecal impaction\n- **No neurologic symptoms** that would suggest an underlying spinal or neuromuscular etiology\n\nManagement of fecal impaction and encopresis involves:\n1. **Disimpaction** (using osmotic or stimulant laxatives, or rectal enemas)\n2. **Maintenance laxative therapy** (e.g., polyethylene glycol/PEG) to prevent re-accumulation\n3. Behavioural and dietary modifications (increased fiber, fluid, and scheduled toilet sitting)\n\n**Why the other options are incorrect:**\n\n- **Surgical intervention** — Not required for simple functional fecal impaction. Surgery is reserved for Hirschsprung disease or other structural pathology, none of which are suggested here.\n\n- **MRI of the spine** — Indicated when neurologic symptoms (e.g., leg weakness, abnormal reflexes, sacral dimpling, saddle anesthesia) suggest a spinal cord lesion as the cause of fecal incontinence. This child has no neurologic features.\n\n- **A barium enema** — Not needed to diagnose functional encopresis; clinical diagnosis is sufficient based on history and examination.\n\n- **Referral to a psychiatrist** — Premature and not indicated when the cause is clearly physiologic (fecal impaction). Encopresis is rarely a primary psychiatric condition in this age group.\n\n**Key Takeaways:**\n- **Encopresis with overflow soiling = fecal impaction until proven otherwise.**\n- The firm left lower quadrant mass is the physical exam finding that clinches the impaction diagnosis.\n- **First-line treatment is laxatives** — not surgery, not imaging, not psychiatry.\n\n**Reference:**\nShen ZY, Zhang J, Bai YZ, Zhang SC. Diagnosis and management of fecal incontinence in children and adolescents. *Frontiers in Pediatrics*. 2022;10:1034240.",
					numFirstSelections: 150,
				},
				{
					id: "c9d0e1f2-9009-4c09-d909-ccddee000005",
					stem: "A barium enema would confirm the diagnosis.",
					text: "A barium enema would confirm the diagnosis.",
					isCorrect: false,
					numFirstSelections: 26,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 7002,
				type: "Skill",
				label: "Medicine",
				breadcrumb: "Medicine",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: ["Encopresis", "Fecal Impaction", "Pediatric Gastroenterology"],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "c9d0e1f2-9009-4c09-d909-ccddee000004",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
	{
		id: 50110,
		type: "MCSS",
		content: {
			stem: "A 32-year-old man is admitted to hospital with severe pancreatitis due to long-term alcohol use disorder. He is treated with rehydration, an analgesic, and nasogastric decompression. After 5 days of treatment, he starts to experience dyspnea. On auscultation, he has normal air entry of the right chest, but he has markedly diminished air entry of the left chest. His left chest is dull to percussion.\n\nWhich one of the following is the most likely diagnosis?",
			choices: [
				{
					id: "d0e1f2a3-0010-4d10-e010-ddeeff110001",
					stem: "Pulmonary embolism",
					text: "Pulmonary embolism",
					isCorrect: false,
					numFirstSelections: 22,
				},
				{
					id: "d0e1f2a3-0010-4d10-e010-ddeeff110002",
					stem: "Pneumothorax",
					text: "Pneumothorax",
					isCorrect: false,
					numFirstSelections: 18,
				},
				{
					id: "d0e1f2a3-0010-4d10-e010-ddeeff110003",
					stem: "Pneumonia",
					text: "Pneumonia",
					isCorrect: false,
					numFirstSelections: 20,
				},
				{
					id: "d0e1f2a3-0010-4d10-e010-ddeeff110004",
					stem: "Heart failure",
					text: "Heart failure",
					isCorrect: false,
					numFirstSelections: 16,
				},
				{
					id: "d0e1f2a3-0010-4d10-e010-ddeeff110005",
					stem: "Pleural effusion",
					text: "Pleural effusion",
					isCorrect: true,
					explanation:
						"**Pleural effusion is the most likely diagnosis.** The clinical presentation is classic: dyspnea developing in the context of acute pancreatitis, with **unilateral diminished air entry and dullness to percussion** of the left chest. These two physical examination findings together are the hallmarks of pleural effusion.\n\nPleural effusion is a well-recognized **complication of acute pancreatitis**. Pancreatic enzymes and inflammatory mediators can track from the inflamed pancreas through the diaphragm into the pleural space, particularly on the left side (the side where the tail of the pancreas lies), causing an **exudative pleural effusion**.\n\n**Differentiating by physical examination:**\n\n| Condition | Percussion | Auscultation |\n|---|---|---|\n| **Pleural effusion** | **Dull** | Diminished |\n| Pneumothorax | Hyperresonant | Diminished |\n| Consolidation (pneumonia) | Dull | Bronchial breath sounds, crackles |\n| Heart failure (bilateral) | Dull (often bilateral) | Bilateral decreased entry |\n\n**Why the other options are incorrect:**\n\n- **Pulmonary embolism** — Typically presents with sudden dyspnea, pleuritic chest pain, and tachycardia. Auscultation and percussion are often **normal** unless infarction occurs. PE does not cause dullness to percussion.\n\n- **Heart failure** — Can cause pleural effusions, but these are typically **bilateral** (right > bilateral > left). The unilateral left-sided finding without mention of right-sided findings makes heart failure less likely than pancreatitis-related effusion.\n\n- **Pneumothorax** — Would present with **hyperresonance** (not dullness) to percussion and absent breath sounds. The percussion finding definitively rules this out.\n\n- **Pneumonia** — Can cause dullness to percussion and decreased breath sounds, but would typically be accompanied by fever, productive cough, and crackles. The clinical context (complication of pancreatitis) and the timing (5 days into admission) favor effusion over de novo pneumonia.\n\n**Key Takeaways:**\n- **Dullness to percussion + decreased breath sounds = fluid in the pleural space (effusion)**. Hyperresonance = air (pneumothorax).\n- Pancreatitis is a recognized cause of left-sided pleural effusion.\n- Confirm with **chest X-ray** (blunting of costophrenic angle) and **thoracentesis** if needed (exudate with elevated amylase suggests pancreatic origin).\n\n**References:**\nBerg M, Shabbir R. Pleural effusion. *Emergency Care BC, Provincial Health Services Authority*. Reviewed October 10, 2023.",
					numFirstSelections: 142,
				},
			],
			instruction: "Choose the best answer",
			highlights: null,
			media: null,
		},
		tags: [
			{
				id: 7002,
				type: "Skill",
				label: "Medicine",
				breadcrumb: "Medicine",
			},
		],
		metadata: {
			type: "MCSS",
			skill: "READING",
			title: "Multiple Choice Single Selection",
			topics: [
				"Pleural Effusion",
				"Pancreatitis Complications",
				"Respiratory Diagnosis",
			],
		},
		response: {
			maximumScore: 1,
			correctResponse: [
				{
					id: "d0e1f2a3-0010-4d10-e010-ddeeff110005",
				},
			],
			numChoicesRequired: {
				max: 1,
				min: 1,
			},
		},
	},
];
